import * as Avatar from '@radix-ui/react-avatar'
import * as Dialog from '@radix-ui/react-dialog'
import { LoaderCircle, X } from 'lucide-react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { initials } from '../lib/format'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

export function Button({
  variant = 'primary',
  loading,
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean }) {
  return (
    <button className={`button button--${variant} ${className}`} disabled={loading || props.disabled} {...props}>
      {loading ? <LoaderCircle aria-hidden="true" className="spin" size={18} /> : null}
      {children}
    </button>
  )
}

export function UserAvatar({ name, src, size = 'md' }: { name: string; src?: string | null; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  return (
    <Avatar.Root className={`avatar avatar--${size}`}>
      <Avatar.Image className="avatar__image" src={src ?? undefined} alt={`Avatar de ${name}`} />
      <Avatar.Fallback className="avatar__fallback" delayMs={100}>{initials(name)}</Avatar.Fallback>
    </Avatar.Root>
  )
}

export function SectionHeader({ eyebrow, title, action }: { eyebrow?: string; title: string; action?: ReactNode }) {
  return (
    <div className="section-header">
      <div>
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h2>{title}</h2>
      </div>
      {action}
    </div>
  )
}

export function StatusPill({ tone = 'neutral', children }: { tone?: 'success' | 'warning' | 'danger' | 'neutral' | 'accent'; children: ReactNode }) {
  return <span className={`status-pill status-pill--${tone}`}>{children}</span>
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  className = '',
  children
}: {
  open: boolean
  onOpenChange(open: boolean): void
  title: string
  description?: string
  className?: string
  children: ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className={`dialog-content ${className}`}>
          <div className="dialog-heading">
            <div>
              <Dialog.Title>{title}</Dialog.Title>
              {description ? <Dialog.Description>{description}</Dialog.Description> : null}
            </div>
            <Dialog.Close className="icon-button" aria-label="Fechar">
              <X size={20} />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function FormField({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <label className="form-field">
      <span className="form-field__label">{label}</span>
      {children}
      {error ? <span className="form-field__error">{error}</span> : hint ? <span className="form-field__hint">{hint}</span> : null}
    </label>
  )
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden="true" />
}

export function InlineError({ message }: { message?: string }) {
  return (
    <div className="inline-error" role="alert">
      <strong>Não foi possível carregar.</strong>
      <span>{message ?? 'Tente novamente em instantes.'}</span>
    </div>
  )
}
